
import { ILevelGeneralListData } from '../../models/level-general-list-data';
import { ILevelGeneralListDto, levelGeneralListDto } from '../../models/level-general-list-dto';
import { LoadingService } from '../loading-services/_loading.service';
import { TabName } from '../page-services/models/tab-name';
import { computed, Injectable, InjectionToken, Signal, signal } from '@angular/core';
import { finalize, first, map, Observable, of } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { tabData } from 'src/app/features/shared/central-page/models/tab-data';

export const LIST_KIND_NAME = new InjectionToken<TabName>('');

@Injectable()
export abstract class _ListService<ListDto extends ILevelGeneralListDto, ListData extends ILevelGeneralListData>
{
    protected abstract readonly _loadingService: LoadingService;
    protected abstract readonly _tabListService: TabListService | null;

    abstract listName: TabName;

    abstract listActive$$: Signal<ListData[]>;
    abstract listActive$: Observable<ListData[]>;
    abstract listDeletedAll$$: Signal<ListData[]>;
    abstract listDeleted$$: Signal<ListData[]>;
    abstract listInactive$$: Signal<ListData[]>;
    abstract listAll$$: Signal<ListData[]>;
    abstract hasDeletedList$$: Signal<boolean>;
    abstract hasInactiveList$$: Signal<boolean>;

    abstract listData_load(listDto: ListDto): ListData | null;
    abstract list_refresh$(): void;
    abstract list_load$(listDataRequestFactory: () => Observable<ListDto[]>): Observable<ListData[]>;

    protected _listActive$$ = computed(() => this._listAll$$().filter(item => !item[levelGeneralListDto.inactive]));
    protected _listDeletedAll$$ = computed(() => this._listAll$$()?.filter(item => item[levelGeneralListDto.inactive]));
    protected _listDeleted$$ = computed(() => this._listDeletedAll$$()?.filter(item => item[levelGeneralListDto.deleted]));
    protected _listInactive$$ = computed(() => this._listDeletedAll$$()?.filter(item => !item[levelGeneralListDto.deleted]));
    protected _listAll$$ = signal<ListData[]>([]);

    protected _listActive$ = toObservable(this._listActive$$);
    protected _hasDeletedList$$ = computed(() => !!this._listDeleted$$() &&  this._listDeleted$$()!.length > 0);
    protected _hasInactiveList$$ = computed(() => !!this._listInactive$$() &&  this._listInactive$$()!.length > 0);

    private __list_set(dtoList: any[]): ListData[] {
        const __listData = dtoList
            .map(item => this.listData_load(item))
            .filter((v): v is ListData => !!v);

        this._listAll$$.set(__listData);

        return __listData;
    }

    protected _list_refresh$(): void {
        const __listAll = this._listAll$$();
        this._listAll$$.set([...__listAll]);
    }

    list_remove(items: ILevelGeneralListData[]): void {
        const __guids = new Set(items.map(item => item.guid));
        this._listAll$$.set(this._listAll$$().filter(item => !__guids.has(item.guid)));
    }

    protected _list_load$(listDataRequestFactory: () => Observable<ListDto[]>): Observable<ListData[]> {
        const __listValue = this._listAll$$();
        const __listIsEmpty = __listValue == null || !Array.isArray(__listValue) || __listValue.length == 0;
        const __needRefresh = this._tabListService?.tabData$$()?.[tabData.needRefresh] ?? true;

        if (!__listIsEmpty && !__needRefresh) {
            return of(__listValue);
        } else {
            // Factory pas hier aanroepen: het bouwen van de request-observable triggert
            // direct ApiService.GET (eager promise). Op een cache-hit mag dat niet gebeuren.
            return this.__listData_request$(listDataRequestFactory()).pipe(
                finalize(() => {
                    const __tabData = this._tabListService?.tabData$$();
                    if (__tabData) { __tabData[tabData.needRefresh] = false; }
                })
            );
        }
    }

    private __listData_request$(listGetRequest$: Observable<ListDto[]>): Observable<ListData[]> {
        return listGetRequest$
            .pipe(
                first(),
                map(list => this.__list_set(list)),
            )
    }
}
